import {
  CheckCircleOutlined,
  DisconnectOutlined,
  SafetyCertificateFilled,
  WarningOutlined,
} from "@ant-design/icons";
import { AutoComplete, Button, Flex, Input, message, Modal, Tag } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { AnnuleerButton } from "~/components/buttons/AnnuleerButton.jsx";
import { ClearSelection } from "~/components/form/ClearSelection.jsx";
import {
  checkFlightRecencyWarningsPIC,
  checkFlightPaxValidity,
  checkRecencyWarnings,
  RecencyCurrencyBarometer,
} from "~/components/recency/index.js";
import { $t, i18n } from "~/i18n.js";
import { UserAvatar } from "./UserAvatar.jsx";
import { selectElementByID } from "~/lib/helpers.js";
import { colors } from "~/theme/colors.js";
import { HelpLine } from "../help/helpLine.jsx";
import "./selectPilot.css";

export const SelectPilotLabel = ({
  vlieger,
  textColor = null,
  showWarning = false,
  enable_authorization = false,
}) => {
  const recencyWarning = checkRecencyWarnings(vlieger);
  const color = textColor || (showWarning && recencyWarning ? "gray" : "black");
  return (
    <div className="SelectPilotLabel row" style={{ color: color, gap: 4 }}>
      <UserAvatar
        user={vlieger}
        className="SelectPilotLabel__Image"
        style={{ backgroundColor: colors.gray_lightest }}
      />{" "}
      <span>{vlieger.name}</span>{" "}
      {vlieger.tag && (
        <Tag style={{ marginLeft: 4, color: color }}>{$t(vlieger.tag)}</Tag>
      )}
      {showWarning &&
        (recencyWarning ||
          (enable_authorization &&
            vlieger.requires_authorisation &&
            !vlieger.authorised)) && (
          <WarningOutlined style={{ color: "#faad14" }} />
        )}
      {showWarning && enable_authorization && vlieger.authorised && (
        <SafetyCertificateFilled style={{ color: colors.groen_dark }} />
      )}
    </div>
  );
};

export const SelectPilot = ({
  pilot,
  otherPilot,
  gezagVoerder,
  title,
  readOnly,
  field_id,
  field_name,
  hideAvatar,
  vliegers,
  dag_id = null,
  alle_vliegers,
  flight,
  saveFlight,
  openPilot,
  showWarning = false,
  enable_authorization = false,
  kist_in_dto,
  savePayingMember = true,
  showFinance = false,
  addUnknown = true,
}) => {
  if (!flight) return null;

  // find and store data on the pilot
  const otherPilotID = otherPilot && otherPilot.id;
  const [name, setName] = useState(flight[field_name]);
  const [result, setResult] = useState(
    vliegers.filter((vlieger) => vlieger.id !== otherPilotID),
  );
  const [searchInAll, setSearchInAll] = useState(false);

  const [showUserForWarning, setShowUserWarning] = useState(null);
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    // initiates every time the props change
    setName(flight[field_name] || (pilot && pilot.name));
    setResult(
      (vliegers.length > 0 ? vliegers : alle_vliegers || []).filter(
        (vlieger) => vlieger.id !== otherPilotID,
      ),
    );
    setSearchInAll(vliegers.length === 0);
  }, [flight, field_name, vliegers, alle_vliegers, otherPilotID]);

  const handleSearch = (searchText, forceSearchAll) => {
    searchText = searchText.toLowerCase();

    // filter out the pilot if it is not the other Pilot
    let filtered = searchInAll || forceSearchAll ? alle_vliegers : vliegers;
    try {
      filtered = filtered.filter(
        (vlieger) =>
          vlieger &&
          vlieger.id !== otherPilotID &&
          vlieger.lowercase &&
          vlieger.lowercase.indexOf(searchText) > -1,
      );
    } catch (e) {
      console.log("UNDEFINED : ", e);
    }

    // if no filtered pilots you cannot continue.
    setResult(filtered);

    return filtered;
  };

  const savePilot = (selectedPilot) => {
    let data = {};
    data[field_name] = selectedPilot.name;
    data[field_id] = selectedPilot.id;

    // always is_fis for known pilots (turn off if DBO is unknown)
    if (!kist_in_dto || !selectedPilot || !otherPilot || !selectedPilot || selectedPilot?.id < 0) {
      data.is_fis = false;
    
    } else {
      
      const possibleInstructor = field_id === "gezagvoerder_id" ? selectedPilot : otherPilot;
    
      if (possibleInstructor.is_fis || possibleInstructor.is_bis) {
        if (
          possibleInstructor.recency &&
          (
            (possibleInstructor.is_fis && possibleInstructor.recency.fis === "invalid")
            ||
            (possibleInstructor.is_bis && possibleInstructor.recency.bis === "invalid")
          )
        ) {
          Modal.warning({
            title: $t("Waarschuwing"),
            content: $t(
              "Let op: deze instructeur heeft een verlopen instructie recency. Vliegen kan maar alleen als passagier.",
            ),
          });
          data.betalend_lid_id = possibleInstructor.id;
          data.is_fis = false;
        } else {
          data.is_fis = true;
        }
      } else {
        data.is_fis = false;
      }
    }

    // showWarning means also that we save the authorisation
    if (field_id == "gezagvoerder_id") {
      data.authorised =
        showWarning && selectedPilot ? selectedPilot.authorised || null : null;
    }

    if (savePayingMember) {
      if (field_id === "gezagvoerder_id") {
        if (!otherPilot) {
          // if no tweede pax, always gezagvoerder
          data.betalend_lid_id = selectedPilot.id;
        } else {
          // determine if current user is instructeur, otherwise it's PIC
          data.betalend_lid_id = data.is_fis ? otherPilot.id : selectedPilot.id;
        }
      } else {
        if (!gezagVoerder) {
          // if no tweede pax, always gezagvoerder
          data.betalend_lid_id = selectedPilot.id;
        } else {
          // determine if current user is instructeur, otherwise it's PIC
          data.betalend_lid_id = data.is_fis
            ? selectedPilot.id
            : gezagVoerder.id;
        }
      }

      // switch tweedeInzittende if gezagvoerder is solo of dbo
      if (
        field_id === "tweede_inzittende_id" &&
        gezagVoerder &&
        ["dbo", "solist"].includes(gezagVoerder.tag) &&
        selectedPilot &&
        selectedPilot.is_instructeur
      ) {
        data.gezagvoerder = selectedPilot;
        data.gezagvoerder_naam = selectedPilot.name;
        data.gezagvoerder_id = selectedPilot.id;
        data.tweede_inzittende = gezagVoerder;
        data.tweede_inzittende_naam = gezagVoerder.name;
        data.tweede_inzittende_id = gezagVoerder.id;
        message.info(
          $t(
            "Vliegers omgewisseld: Instructeur dient gezagvoerder te zijn als die met een DBO/Solist vliegt.",
          ),
        );

        // add fis only if selectedPilot has valid recency
        if (selectedPilot.recency && selectedPilot.recency.fis === "invalid") {
          data.is_fis = false;
          data.betalend_lid_id = selectedPilot.id;
        } else {
          data.is_fis = true;
          data.betalend_lid_id = otherPilot.id;
        }
      }
    }

    saveFlight(data);
  };

  const onSelect = (name) => {
    if (name === "---") {
      // open modal to enter guest pilot name
      setGastVliegerNaam("");
      setGastModal(true);
      setName(null);
      return;
    }

    if (!name) {
      const data = {};
      data[field_name] = "";
      data[field_id] = "DELETE";
      data.betalend_lid_id = otherPilot ? otherPilot.id : "DELETE";
      data.is_fis = false;
      setName(null);
      saveFlight(data);
    } else {
      // it can either be the name you type, or the selected value you have
      // e.g. 1-Boris Schellekens if so, then you only add the second bit.
      const split = name.split("::");
      const id = parseInt(split[0]);
      let selectedPilot =
        id > 0 &&
        selectElementByID(searchInAll ? alle_vliegers : vliegers, "id", id);

      // buildin make sure that you show warning if required
      // For authorisation, if rececency warnings and not authorised, show warnings
      let hasWarnings = false;
      let warning = null;
      const pilotToCheckForWarnings =
        field_id === "gezagvoerder_id" ? selectedPilot : otherPilot;

      if (showWarning) {
        if (field_id === "gezagvoerder_id" && selectedPilot && showWarning) {
          hasWarnings = checkFlightRecencyWarningsPIC(selectedPilot, flight);
          warning = $t(
            "Deze vlieger heeft mogelijk SPL recency waarschuwingen",
          );
        } else if (
          field_id === "tweede_inzittende_id" &&
          otherPilot &&
          showWarning
        ) {
          hasWarnings = checkFlightPaxValidity(otherPilot);
          warning = $t("Deze vlieger mag mogelijk geen passagiers vliegen");
        }

        // also check for authorisation, if enabled
        if (enable_authorization) {
          if (
            (hasWarnings || pilotToCheckForWarnings.requires_authorisation) &&
            !pilotToCheckForWarnings.authorised
          ) {
            hasWarnings = true;
            warning = $t(
              "Deze vlieger heeft mogelijk geen authorisatie om te vliegen",
            );
          }
        }

        // also show warnings if Pilot in command and groups are not correct ("niet_vliegend", "brevet", "instructeur", "basic_instructor")
        const keys = selectedPilot.group_keys;
        if (keys) {
          if (field_id === "gezagvoerder_id") {
            if (selectedPilot.group_keys?.includes("niet_vliegend")) {
              hasWarnings = true;
              warning = $t("Deze vlieger mag mogelijk niet vliegen");
            }
            const allowedGroups = [
              "solist",
              "brevet",
              "instructeur",
              "basic_instructor",
            ];
            if (
              !allowedGroups.some((group) =>
                selectedPilot.group_keys?.includes(group),
              )
            ) {
              hasWarnings = true;
              warning = $t("Deze vlieger mag mogelijk geen gezagvoerder zijn");
            }
          }
        }
      }

      if (selectedPilot && hasWarnings) {
        setName("");
        setWarning(warning);
        return setShowUserWarning(pilotToCheckForWarnings);
      }
      if (!selectedPilot) selectedPilot = { id, name: split[1] };
      savePilot(selectedPilot);
    }
  };

  const clickable = pilot && pilot.id > 0 && openPilot;

  const inputRef = useRef(null);

  const [gastVliegerNaam, setGastVliegerNaam] = useState("");
  const [gastVliegerNaamModalVisible, setGastModal] = useState(false);
  const setGastVlieger = () => {
    if (gastVliegerNaam && gastVliegerNaam.trim().length > 0) {
      onSelect(`-1::${gastVliegerNaam.trim()}`);
      setGastModal(false);
      setGastVliegerNaam("");
      if (inputRef.current) inputRef.current.blur();
    }
  };

  return (
    <div className="selectPilot row">
      {!hideAvatar && (
        <UserAvatar
          user={pilot}
          className={`${clickable ? "clickable" : ""} pilotAvatar`}
          onClick={() => clickable && openPilot && openPilot()}
        />
      )}

      {/* Modal for guest pilot name */}
      <Modal
        open={gastVliegerNaamModalVisible}
        title={$t("Voer (gast)vlieger naam in")}
        onCancel={() => setGastModal(false)}
        onOk={setGastVlieger}
        okButtonProps={{
          disabled: !gastVliegerNaam || gastVliegerNaam.trim().length === 0,
        }}
      >
        <HelpLine>
          {$t(
            "Voer de naam van de (gast)vlieger in als de vlieger niet in het systeem staat. Deze naam wordt gebruikt om de vlucht te registreren.",
          )}
        </HelpLine>

        <Input
          style={{ marginTop: 21 }}
          autoFocus
          value={gastVliegerNaam}
          onChange={(e) => setGastVliegerNaam(e.target.value)}
          placeholder={$t("Naam")}
          onPressEnter={setGastVlieger}
        />
      </Modal>

      {/* Confirm modal for user warnings */}
      <Modal
        width={340}
        title={
          <>
            <Tag color="orange" icon={<WarningOutlined />} />{" "}
            {$t("Bevestig vlieger")}
          </>
        }
        open={!!showUserForWarning?.id}
        onCancel={() => {
          setShowUserWarning(null);
          setWarning(null);
        }}
        footer={[
          <Button
            key="slaan"
            danger
            onClick={() => {
              savePilot(showUserForWarning);
              setShowUserWarning(null);
              setWarning(null);
            }}
            icon={<CheckCircleOutlined />}
          >
            {$t("Bevestig")}
          </Button>,
          <AnnuleerButton
            key="annuleren"
            onClick={() => {
              setShowUserWarning(null);
              setWarning(null);
            }}
          />,
        ]}
      >
        <Flex vertical gap={12}>
          {warning && <p style={{ marginBottom: 12 }}>{warning}</p>}

          <p>
            <span className="bold">{$t("Startmethode")}:</span>{" "}
            {$t(flight.start_methode)}
          </p>

          <p className="bold">{$t("Gezagvoerder")}:</p>
          <SelectPilotLabel vlieger={showUserForWarning} textColor="black" />

          <div style={{ marginLeft: 12 }}>
            <RecencyCurrencyBarometer pilot={showUserForWarning} />
          </div>
        </Flex>
      </Modal>

      <div
        className="column"
        style={{
          alignItems: "flex-start",
          marginLeft: 8,
          position: "relative",
        }}
      >
        <div className="row" style={{ alignItems: "center", gap: 4 }}>
          <AutoComplete
            value={name}
            onChange={(name) => {
              if (name) {
                // it can either be the name you type, or the selected value you have
                // e.g. 1-Boris Schellekens if so, then you only add the second bit.
                const split = name.split("::");
                setName(split.length > 1 ? split[1] : split[0]);
              } else {
                // clear pilot
                setName(null);
                onSelect(null);
              }
            }}
            allowClear={false}
            popupMatchSelectWidth={false}
            disabled={readOnly || name === "-"}
            onSelect={onSelect}
            onSearch={handleSearch}
            dropdownRender={(menu) => (
              <>
                {menu}
                {name && alle_vliegers && !searchInAll && result.length > 0 && (
                  <div style={{ padding: "4px 8px 4px" }}>
                    <Button
                      type="default"
                      block
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchInAll(true);
                        handleSearch(name, true);
                      }}
                    >
                      {$t("Zoek in alle leden")}
                    </Button>
                  </div>
                )}
              </>
            )}
            options={(!name && addUnknown
              ? [
                  {
                    label: (
                      <Button type="primary" block size="small">
                        {$t("Voer (gast)vlieger naam")}
                      </Button>
                    ),
                    value: "---",
                  },
                ]
              : []
            ).concat(
              result.reduce(
                (all, vlieger) =>
                  all.concat({
                    label: SelectPilotLabel({
                      vlieger,
                      showWarning,
                      enable_authorization,
                    }),
                    value: vlieger.id + "::" + vlieger.name,
                  }),
                [],
              ),
            )}
            notFoundContent={
              <div
                style={{
                  gap: 8,
                  maxWidth: 200,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Add unknown name button if no name is typed */}
                {!name && addUnknown && (
                  <Button
                    type="primary"
                    block
                    onClick={() => {
                      onSelect(`-1::[onbekend]`);
                      if (inputRef.current) inputRef.current.blur();
                    }}
                  >
                    {$t("Voeg onbekende naam toe")}
                  </Button>
                )}

                {alle_vliegers && (
                  <Button
                    type="default"
                    block
                    disabled={!name || readOnly}
                    onClick={() => {
                      setSearchInAll(!searchInAll);
                      let result = handleSearch(name, true);
                      if (result.length === 0) {
                        message.error($t("Geen vliegers gevonden"));
                      }
                    }}
                  >
                    {$t("Zoek in alle leden")}
                  </Button>
                )}

                {dag_id && (
                  <Button block target="_blank" href={`/register/${dag_id}`}>
                    {$t("Meld een lid aan")}
                  </Button>
                )}

                {/* Add pilot by name if not found */}
                {addUnknown && (
                  <Button
                    type="primary"
                    block
                    disabled={!name || readOnly}
                    onClick={() => {
                      if (name) onSelect(`-1::${name}`);
                      if (inputRef.current) inputRef.current.blur();
                    }}
                  >
                    {$t('Voeg "${name}" toe', { name })}
                  </Button>
                )}
              </div>
            }
          >
            <Input
              placeholder={title}
              onPressEnter={() =>
                result.length > 0 && onSelect(result[0].id.toString())
              }
              ref={inputRef}
            />
          </AutoComplete>
          <ClearSelection
            disabled={readOnly || !name}
            onClick={() => {
              setName(null);
              onSelect(null);
            }}
          />
          {showFinance && pilot && otherPilot && (
            <Button
              shape="circle"
              size="small"
              disabled={!pilot}
              onClick={() => {
                if (pilot) {
                  saveFlight({
                    betalend_lid_id: pilot.id,
                  });
                }
              }}
              type={
                flight.betalend_lid_id &&
                pilot &&
                pilot.id === flight.betalend_lid_id
                  ? "primary"
                  : "default"
              }
            >
              {i18n.currencySymbol}
            </Button>
          )}
        </div>

        {!hideAvatar && pilot && (
          <div
            className={`column indicator ${clickable && "clickable"}`}
            onClick={() => clickable && openPilot()}
          >
            <RecencyCurrencyBarometer
              pilot={pilot}
              tag={pilot?.tag}
              skipDetails
              showAuthorisation={enable_authorization}
              authorisation={flight?.authorised}
            />
          </div>
        )}

        {!pilot && flight[field_name] && (
          <Tag icon={<DisconnectOutlined />} style={{ marginTop: 4 }}>
            {$t("Geen clublid")}
          </Tag>
        )}
      </div>
    </div>
  );
};
